var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
// helpers
import { LOGIN_TYPE } from '@/constants/login';
import { getHostname, getOzoneServiceBasePath } from '@/helpers/endpoints';
import { generateCodeChallenge } from '@/helpers/login';
import { readFromLocalStorage, writeToLocalStorage } from '@/helpers/storage';
import HttpClient from '@/includes/httpClient';
import { EnvConfigMap, getEnvironmentViaHostname } from '@/helpers/env';
import { getErrorMessage as getErrorMessageSendOtp } from '@/helpers/apis/SendOtp';
import { getErrorMessage as getErrorMessageSignInWithOtp } from '@/helpers/apis/SignInWithOtp';
import { handleError } from '@/helpers/apis';
// constants
import { localStorageKeys } from '@/constants/storage';
import { loginApiPaths, CODE_CHALLENGE_ALGORITHM, PROVIDER, REDIRECT_URI, HOST_NOT_FOUND_ERROR, } from './constants';
// LOCAL HELPER FUNCTIONS ================================ STARTS HERE
const saveChallengeAndVerifier = (codeChallenge, codeVerifier) => __awaiter(void 0, void 0, void 0, function* () {
    yield writeToLocalStorage(localStorageKeys.CODE_CHALLENGE, codeChallenge);
    yield writeToLocalStorage(localStorageKeys.CODE_VERIFIER, codeVerifier);
});
const saveTokens = (access_token, refresh_token) => __awaiter(void 0, void 0, void 0, function* () {
    yield writeToLocalStorage(localStorageKeys.ACCESS_TOKEN, access_token);
    yield writeToLocalStorage(localStorageKeys.REFRESH_TOKEN, refresh_token);
});
const getOzoneHeaderUserType = (loginType) => {
    return loginType === LOGIN_TYPE.INTERNAL ? 'EMPLOYEE' : 'BRAND';
};
// LOCAL HELPER FUNCTIONS ================================ ENDS HERE
// INITIATE LOGIN API LOGIC ============================== STARTS HERE
const generateInitiateLoginApiPayload = () => __awaiter(void 0, void 0, void 0, function* () {
    const { codeChallenge, codeVerifier } = yield generateCodeChallenge();
    yield saveChallengeAndVerifier(codeChallenge, codeVerifier);
    const clientId = EnvConfigMap[getEnvironmentViaHostname()].clientId;
    const payload = {
        provider: PROVIDER,
        client_id: clientId,
        redirect_uri: `${getHostname()}${REDIRECT_URI}`,
        pkce: {
            code_challenge: codeChallenge,
            code_challenge_algorithm: CODE_CHALLENGE_ALGORITHM,
        },
    };
    return payload;
});
export const initiateLogingApi = (loginType) => __awaiter(void 0, void 0, void 0, function* () {
    try {
        const requestPayload = yield generateInitiateLoginApiPayload();
        const host = getOzoneServiceBasePath(loginType);
        if (host == null) {
            throw new Error(HOST_NOT_FOUND_ERROR);
        }
        const headers = {
            'X-Ozone-User': getOzoneHeaderUserType(loginType),
        };
        const url = `${host}${loginApiPaths.initiateLogin}`;
        const response = yield HttpClient.post(url, Object.assign({}, requestPayload), headers);
        yield writeToLocalStorage(localStorageKeys.LOGIN_TYPE, loginType);
        return response.authUri;
    }
    catch (error) {
        if (__DEV__) {
            console.log(error);
        }
    }
});
// INITIATE LOGIN API LOGIC ============================== ENDS HERE
// SIGN IN WITH IDP LOGIC ================================ STARTS HERE
const generateSignInWithIDPPayload = (code, code_verifier) => {
    const clientId = EnvConfigMap[getEnvironmentViaHostname()].clientId;
    return {
        provider: PROVIDER,
        client_id: clientId,
        redirect_uri: `${getHostname()}${REDIRECT_URI}`,
        code, // this needs to be read from the url params (sent by the IDP after successful login)
        code_verifier, // this is precomputed on the client and saved in the local storage
    };
};
export const signInWithIDP = (code, cb) => __awaiter(void 0, void 0, void 0, function* () {
    try {
        const loginType = yield readFromLocalStorage(localStorageKeys.LOGIN_TYPE);
        const codeVerifier = yield readFromLocalStorage(localStorageKeys.CODE_VERIFIER);
        if (!codeVerifier || !loginType) {
            throw new Error('code_verifier or loginType not found');
        }
        const host = getOzoneServiceBasePath(loginType);
        if (host == null) {
            throw new Error(HOST_NOT_FOUND_ERROR);
        }
        const requestPayload = generateSignInWithIDPPayload(code, codeVerifier);
        const headers = {
            'X-Ozone-User': getOzoneHeaderUserType(loginType),
        };
        const url = `${host}${loginApiPaths.signInWithIDP}`;
        const response = yield HttpClient.post(url, Object.assign({}, requestPayload), headers);
        const { accessToken, refreshToken } = response;
        yield saveTokens(accessToken, refreshToken);
        cb();
    }
    catch (error) {
        if (__DEV__) {
            console.error(error);
        }
        throw error;
        // capture error and show it to the user
    }
});
export const initiateRefreshToken = (userId, refreshToken) => __awaiter(void 0, void 0, void 0, function* () {
    try {
        const loginType = yield readFromLocalStorage(localStorageKeys.LOGIN_TYPE);
        const accessToken = yield readFromLocalStorage(localStorageKeys.ACCESS_TOKEN);
        if (!loginType || !accessToken) {
            throw new Error('login type missing');
        }
        const host = getOzoneServiceBasePath(loginType);
        const clientId = EnvConfigMap[getEnvironmentViaHostname()].clientId;
        if (host == null) {
            throw new Error(HOST_NOT_FOUND_ERROR);
        }
        const url = `${host}${loginApiPaths.refreshToken}`;
        const headers = {
            Authorization: `Bearer ${accessToken}`,
            'X-Ozone-User': getOzoneHeaderUserType(loginType),
        };
        const requestPayload = {
            user_id: userId,
            refresh_token: refreshToken,
            client_id: clientId,
        };
        const response = yield HttpClient.post(url, Object.assign({}, requestPayload), headers);
        yield saveTokens(response.access_token, response.refresh_token);
    }
    catch (error) {
        if (__DEV__) {
            console.error(error);
        }
    }
});
// REFRESH TOKEN LOGIC ==================================== ENDS HERE
export const sendOtp = (email) => __awaiter(void 0, void 0, void 0, function* () {
    const host = getOzoneServiceBasePath(LOGIN_TYPE.EXTERNAL);
    const clientId = EnvConfigMap[getEnvironmentViaHostname()].clientId;
    if (host == null) {
        throw new Error(HOST_NOT_FOUND_ERROR);
    }
    const url = `${host}${loginApiPaths.sendVerificationCode}`;
    const requestPayload = {
        email,
        client_id: clientId,
    };
    try {
        const response = yield HttpClient.post(url, Object.assign({}, requestPayload));
        return { sessionInfo: response.session_info, userId: response.user_id };
    }
    catch (error) {
        yield handleError(error, getErrorMessageSendOtp);
    }
});
export const signInWithOTP = (otp, userId, sessionInfo, cb) => __awaiter(void 0, void 0, void 0, function* () {
    try {
        const host = getOzoneServiceBasePath(LOGIN_TYPE.EXTERNAL);
        const clientId = EnvConfigMap[getEnvironmentViaHostname()].clientId;
        if (host == null) {
            throw new Error(HOST_NOT_FOUND_ERROR);
        }
        const url = `${host}${loginApiPaths.signInWithOTP}`;
        const requestPayload = {
            otp,
            user_id: userId,
            session_info: sessionInfo,
            client_id: clientId,
        };
        const response = yield HttpClient.post(url, Object.assign({}, requestPayload));
        yield saveTokens(response.access_token, response.refresh_token);
        cb();
    }
    catch (error) {
        yield handleError(error, getErrorMessageSignInWithOtp);
    }
});
